import React, { useEffect, useState, useCallback } from "react";
import MetaTags from "react-meta-tags";
import {
  Container, Row, Col, Card, CardBody, Spinner, Badge, ButtonGroup, Button
} from "reactstrap";
import { get } from "../../helpers/api_helper";
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from "recharts";
import "./dashboard.css";

// ── Color Palette ─────────────────────────────────────────────────────────────
const COLORS = ["#6366f1", "#22d3ee", "#f59e0b", "#10b981", "#f43f5e", "#8b5cf6", "#06b6d4"];
const STATUS_COLORS = {
  "Under Contract": "#6366f1",
  "Closed": "#10b981",
  "Active": "#22d3ee",
  "Pending": "#f59e0b",
  "Cancelled": "#f43f5e",
  "Unknown": "#94a3b8",
};

// ── Compact Stat Card ─────────────────────────────────────────────────────────
const StatCard = ({ icon, title, value, sub, trend, color, loading }) => (
  <Card className="dash-stat-card h-100">
    <CardBody className="p-3">
      <div className="d-flex align-items-center justify-content-between">
        <div className="d-flex align-items-center gap-3">
          <div className={`dash-icon-wrap bg-${color}-soft`}>
            <i className={`${icon} dash-icon text-${color}`} />
          </div>
          <div>
            <span className="dash-stat-title">{title}</span>
            {loading ? (
              <div><Spinner size="sm" color="secondary" /></div>
            ) : (
              <h4 className="dash-stat-value">{(value ?? 0).toLocaleString()}</h4>
            )}
          </div>
        </div>
        {trend !== undefined && (
          <Badge
            color={trend >= 0 ? "success" : "danger"}
            className="dash-trend-badge"
          >
            {trend >= 0 ? "▲" : "▼"} {Math.abs(trend)}%
          </Badge>
        )}
      </div>
      {sub && <p className="dash-stat-sub">{sub}</p>}
    </CardBody>
  </Card>
);

// ── Custom Tooltip ─────────────────────────────────────────────────────────────
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="dash-tooltip">
        <p className="dash-tooltip-label">{label}</p>
        {payload.map((p, i) => (
          <p key={i} style={{ color: p.color }} className="mb-0">
            {p.name}: <strong>{p.value?.toLocaleString()}</strong>
          </p>
        ))}
      </div>
    );
  }
  return null;
};

// ── Donut Label ───────────────────────────────────────────────────────────────
const RADIAN = Math.PI / 180;
const renderCustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
  if (percent < 0.08) return null;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  return (
    <text x={x} y={y} fill="white" textAnchor="middle" dominantBaseline="central" fontSize={10} fontWeight={600}>
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
};

// ── Format month label ─────────────────────────────────────────────────────────
const fmtMonth = (m) => {
  if (!m) return "";
  const [y, mo] = m.split("-");
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${months[parseInt(mo) - 1]} '${y.slice(2)}`;
};

const MOCK_DASHBOARD_DATA = {
  overview: {
    totalEntities: 12,
    totalUsers: 48,
    totalDeals: 135,
    totalContacts: 310,
    totalTasks: 84,
    totalTemplates: 15,
    universalTemplates: 6,
    universalChecklistSteps: 45,
    newUsersThisMonth: 6,
    closedDealsThisMonth: 12,
    newContactsThisMonth: 28,
    dealsGrowth: 15,
  },
  deals: {
    monthlyTrend: [
      { month: "2026-01", count: 12 },
      { month: "2026-02", count: 18 },
      { month: "2026-03", count: 15 },
      { month: "2026-04", count: 22 },
      { month: "2026-05", count: 28 },
      { month: "2026-06", count: 32 },
      { month: "2026-07", count: 40 },
      { month: "2026-08", count: 35 },
    ],
    byStatus: [
      { status: "Under Contract", count: 42 },
      { status: "Closed", count: 48 },
      { status: "Active", count: 25 },
      { status: "Pending", count: 14 },
      { status: "Cancelled", count: 6 },
    ],
    byType: [
      { type: "Purchase", count: 65 },
      { type: "Listing", count: 45 },
      { type: "Lease", count: 25 },
    ],
  },
  contacts: {
    monthlyTrend: [
      { month: "2026-01", count: 25 },
      { month: "2026-02", count: 40 },
      { month: "2026-03", count: 65 },
      { month: "2026-04", count: 90 },
      { month: "2026-05", count: 130 },
      { month: "2026-06", count: 180 },
      { month: "2026-07", count: 240 },
      { month: "2026-08", count: 310 },
    ],
    bySource: [
      { source: "Google", count: 120 },
      { source: "Referral", count: 95 },
      { source: "Website", count: 65 },
      { source: "Direct", count: 30 },
    ],
  },
  tasks: {
    completed: 58,
    pending: 26,
  },
  topEntities: [
    { entity_id: 1, entity_title: "Acme Real Estate", entity_type: 3, deal_count: 45 },
    { entity_id: 2, entity_title: "Apex Realty Group", entity_type: 1, deal_count: 32 },
    { entity_id: 3, entity_title: "Horizon Properties", entity_type: 2, deal_count: 24 },
    { entity_id: 4, entity_title: "Bayview Brokerage", entity_type: 1, deal_count: 18 },
  ],
  recentEntities: [
    { entity_id: 1, entity_title: "Acme Real Estate", contact_email: "contact@acme.com", created_at: "2026-08-01" },
    { entity_id: 2, entity_title: "Apex Realty Group", contact_email: "info@apex.com", created_at: "2026-08-05" },
    { entity_id: 3, entity_title: "Horizon Properties", contact_email: "admin@horizon.com", created_at: "2026-08-10" },
    { entity_id: 4, entity_title: "Bayview Brokerage", contact_email: "support@bayview.com", created_at: "2026-08-12" },
  ],
};

// ── Main Dashboard ─────────────────────────────────────────────────────────────
const Dashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [trendTab, setTrendTab] = useState("deals");
  const [distTab, setDistTab] = useState("deals");

  const fetchDashboard = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await get("/superadmin/dashboard");
      setData(res?.data || res);
    } catch (e) {
      console.warn("Failed to load backend dashboard data, using static fallback", e);
      setData(MOCK_DASHBOARD_DATA);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  const ov = data?.overview || {};
  const deals = data?.deals || {};
  const contacts = data?.contacts || {};
  const tasks = data?.tasks || {};

  const currentTrendData = trendTab === "deals"
    ? (deals.monthlyTrend || []).map(d => ({ ...d, month: fmtMonth(d.month) }))
    : (contacts.monthlyTrend || []).map(d => ({ ...d, month: fmtMonth(d.month) }));

  const currentTrendColor = trendTab === "deals" ? "#6366f1" : "#22d3ee";
  const currentTrendName = trendTab === "deals" ? "Deals" : "Contacts";

  const currentDistData = distTab === "deals" ? (deals.byStatus || []) : (contacts.bySource || []);
  const distKey = distTab === "deals" ? "status" : "source";

  return (
    <React.Fragment>
      <div className="dash-page ms-4 me-4">
        <MetaTags>
          <title>Dashboard | {process.env.REACT_APP_TITLE}</title>
        </MetaTags>

        <Container fluid>
          {/* ── Header ──────────────────────────────────────────────── */}
          <div className="dash-header mb-3">
            <div>
              <h4 className="dash-title">Platform Overview</h4>
              <p className="dash-subtitle">Real-time analytics across all entities</p>
            </div>
            <button className="dash-refresh-btn" onClick={fetchDashboard} title="Refresh">
              <i className="bx bx-refresh" />
            </button>
          </div>

          {error && (
            <div className="alert alert-danger py-2 mb-3">{error}</div>
          )}

          {/* ── KPI Cards: Row 1 (Primary Metrics) ─────────────────── */}
          <Row className="g-2 mb-2">
            <Col xl={3} md={6}>
              <StatCard
                icon="bx bxs-buildings"
                title="Total Companies"
                value={ov.totalEntities}
                sub={`+${ov.newUsersThisMonth ?? 0} users this month`}
                color="primary"
                loading={loading}
              />
            </Col>
            <Col xl={3} md={6}>
              <StatCard
                icon="bx bxs-user-account"
                title="Total Users"
                value={ov.totalUsers}
                color="info"
                loading={loading}
              />
            </Col>
            <Col xl={3} md={6}>
              <StatCard
                icon="bx bxs-briefcase"
                title="Total Deals"
                value={ov.totalDeals}
                sub={`${ov.closedDealsThisMonth ?? 0} closed this month`}
                trend={ov.dealsGrowth}
                color="success"
                loading={loading}
              />
            </Col>
            <Col xl={3} md={6}>
              <StatCard
                icon="bx bxs-contact"
                title="Total Contacts"
                value={ov.totalContacts}
                sub={`+${ov.newContactsThisMonth ?? 0} this month`}
                color="warning"
                loading={loading}
              />
            </Col>
          </Row>

          {/* ── KPI Cards: Row 2 (Secondary Metrics) ───────────────── */}
          <Row className="g-2 mb-3">
            <Col xl={3} md={6}>
              <StatCard
                icon="bx bx-task"
                title="Total Tasks"
                value={ov.totalTasks}
                sub={`${tasks.pending ?? 0} pending · ${tasks.completed ?? 0} done`}
                color="danger"
                loading={loading}
              />
            </Col>
            <Col xl={3} md={6}>
              <StatCard
                icon="bx bx-file"
                title="User Templates"
                value={ov.totalTemplates}
                color="purple"
                loading={loading}
              />
            </Col>
            <Col xl={3} md={6}>
              <StatCard
                icon="bx bx-globe"
                title="Universal Templates"
                value={ov.universalTemplates}
                color="cyan"
                loading={loading}
              />
            </Col>
            <Col xl={3} md={6}>
              <StatCard
                icon="bx bx-list-check"
                title="Checklist Steps"
                value={ov.universalChecklistSteps}
                color="teal"
                loading={loading}
              />
            </Col>
          </Row>

          {/* ── Row 1: Activity Trends + Distribution Breakdown ───────── */}
          <Row className="g-3 mb-3">
            {/* Trends Chart (Toggleable Deals / Contacts) */}
            <Col lg={8}>
              <Card className="dash-chart-card h-100">
                <CardBody className="p-3">
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <h6 className="dash-chart-title mb-0">
                      {trendTab === "deals" ? "Deals Created" : "Contacts Growth"} — Last 12 Months
                    </h6>
                    <ButtonGroup size="sm" className="dash-pill-group">
                      <Button
                        color={trendTab === "deals" ? "primary" : "light"}
                        className="dash-pill-btn"
                        onClick={() => setTrendTab("deals")}
                      >
                        Deals Trend
                      </Button>
                      <Button
                        color={trendTab === "contacts" ? "primary" : "light"}
                        className="dash-pill-btn"
                        onClick={() => setTrendTab("contacts")}
                      >
                        Contacts Growth
                      </Button>
                    </ButtonGroup>
                  </div>
                  {loading ? (
                    <div className="dash-chart-loading"><Spinner color="primary" /></div>
                  ) : (
                    <ResponsiveContainer width="100%" height={200}>
                      <AreaChart data={currentTrendData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                        <defs>
                          <linearGradient id="trendGrad" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor={currentTrendColor} stopOpacity={0.25} />
                            <stop offset="95%" stopColor={currentTrendColor} stopOpacity={0.0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                        <XAxis dataKey="month" tick={{ fill: "#94a3b8", fontSize: 11 }} axisLine={false} tickLine={false} />
                        <YAxis tick={{ fill: "#94a3b8", fontSize: 11 }} allowDecimals={false} axisLine={false} tickLine={false} />
                        <Tooltip content={<CustomTooltip />} />
                        <Area
                          type="monotone"
                          dataKey="count"
                          name={currentTrendName}
                          stroke={currentTrendColor}
                          fill="url(#trendGrad)"
                          strokeWidth={2.5}
                          dot={{ r: 3, fill: currentTrendColor }}
                          activeDot={{ r: 5 }}
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  )}
                </CardBody>
              </Card>
            </Col>

            {/* Distribution Breakdown (Toggleable Status / Source) */}
            <Col lg={4}>
              <Card className="dash-chart-card h-100">
                <CardBody className="p-3 d-flex flex-column justify-content-between">
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <h6 className="dash-chart-title mb-0">
                      {distTab === "deals" ? "Deals by Status" : "Contacts by Source"}
                    </h6>
                    <ButtonGroup size="sm" className="dash-pill-group">
                      <Button
                        color={distTab === "deals" ? "primary" : "light"}
                        className="dash-pill-btn"
                        onClick={() => setDistTab("deals")}
                      >
                        Status
                      </Button>
                      <Button
                        color={distTab === "contacts" ? "primary" : "light"}
                        className="dash-pill-btn"
                        onClick={() => setDistTab("contacts")}
                      >
                        Source
                      </Button>
                    </ButtonGroup>
                  </div>
                  {loading ? (
                    <div className="dash-chart-loading"><Spinner color="primary" /></div>
                  ) : (
                    <div className="dash-dist-wrap">
                      <div className="dash-donut-box">
                        <ResponsiveContainer width={130} height={130}>
                          <PieChart>
                            <Pie
                              data={currentDistData}
                              dataKey="count"
                              nameKey={distKey}
                              cx="50%"
                              cy="50%"
                              innerRadius={38}
                              outerRadius={60}
                              paddingAngle={2}
                              labelLine={false}
                              label={renderCustomLabel}
                            >
                              {currentDistData.map((entry, i) => (
                                <Cell
                                  key={i}
                                  fill={STATUS_COLORS[entry[distKey]] || COLORS[i % COLORS.length]}
                                />
                              ))}
                            </Pie>
                            <Tooltip formatter={(v, n) => [v, n]} />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                      <div className="dash-legend-compact">
                        {currentDistData.map((s, i) => (
                          <div key={i} className="dash-legend-item">
                            <span
                              className="dash-legend-dot"
                              style={{ background: STATUS_COLORS[s[distKey]] || COLORS[i % COLORS.length] }}
                            />
                            <span className="dash-legend-label">{s[distKey]}</span>
                            <strong className="dash-legend-val">{s.count}</strong>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* ── Row 2: Deals by Type + Tasks Overview + Top Entities ────── */}
          <Row className="g-3 mb-3">
            {/* Deals by Type */}
            <Col lg={4}>
              <Card className="dash-chart-card h-100">
                <CardBody className="p-3">
                  <h6 className="dash-chart-title mb-2">Deals by Type</h6>
                  {loading ? (
                    <div className="dash-chart-loading"><Spinner color="primary" /></div>
                  ) : (
                    <ResponsiveContainer width="100%" height={170}>
                      <BarChart data={deals.byType || []} barSize={28} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                        <XAxis dataKey="type" tick={{ fill: "#94a3b8", fontSize: 11 }} axisLine={false} tickLine={false} />
                        <YAxis tick={{ fill: "#94a3b8", fontSize: 11 }} allowDecimals={false} axisLine={false} tickLine={false} />
                        <Tooltip content={<CustomTooltip />} />
                        <Bar dataKey="count" name="Deals" radius={[4, 4, 0, 0]}>
                          {(deals.byType || []).map((_, i) => (
                            <Cell key={i} fill={COLORS[i % COLORS.length]} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </CardBody>
              </Card>
            </Col>

            {/* Tasks Overview */}
            <Col lg={4}>
              <Card className="dash-chart-card h-100">
                <CardBody className="p-3">
                  <h6 className="dash-chart-title mb-2">Tasks Overview</h6>
                  {loading ? (
                    <div className="dash-chart-loading"><Spinner color="primary" /></div>
                  ) : (
                    <div className="dash-task-overview-compact">
                      <div className="dash-task-ring-compact">
                        <ResponsiveContainer width={110} height={110}>
                          <PieChart>
                            <Pie
                              data={[
                                { name: "Completed", value: tasks.completed || 0 },
                                { name: "Pending", value: tasks.pending || 0 },
                              ]}
                              dataKey="value"
                              cx="50%"
                              cy="50%"
                              innerRadius={36}
                              outerRadius={50}
                              startAngle={90}
                              endAngle={-270}
                            >
                              <Cell fill="#10b981" />
                              <Cell fill="#e2e8f0" />
                            </Pie>
                          </PieChart>
                        </ResponsiveContainer>
                        <div className="dash-task-center">
                          <span className="dash-task-pct">
                            {(tasks.completed || 0) + (tasks.pending || 0) > 0
                              ? Math.round(((tasks.completed || 0) / ((tasks.completed || 0) + (tasks.pending || 0))) * 100)
                              : 0}%
                          </span>
                          <span className="dash-task-label">Done</span>
                        </div>
                      </div>

                      <div className="dash-task-stats-compact">
                        <div className="dash-task-stat-mini">
                          <div className="dash-task-stat-icon bg-success-soft">
                            <i className="bx bx-check-circle text-success" />
                          </div>
                          <div>
                            <h6>{(tasks.completed || 0).toLocaleString()}</h6>
                            <p>Completed</p>
                          </div>
                        </div>
                        <div className="dash-task-stat-mini">
                          <div className="dash-task-stat-icon bg-warning-soft">
                            <i className="bx bx-time-five text-warning" />
                          </div>
                          <div>
                            <h6>{(tasks.pending || 0).toLocaleString()}</h6>
                            <p>Pending</p>
                          </div>
                        </div>
                        <div className="dash-task-stat-mini">
                          <div className="dash-task-stat-icon bg-primary-soft">
                            <i className="bx bx-list-ul text-primary" />
                          </div>
                          <div>
                            <h6>{((tasks.completed || 0) + (tasks.pending || 0)).toLocaleString()}</h6>
                            <p>Total Tasks</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>

            {/* Top Companies by Deals */}
            <Col lg={4}>
              <Card className="dash-chart-card h-100">
                <CardBody className="p-3">
                  <h6 className="dash-chart-title mb-2">Top Companies by Deals</h6>
                  {loading ? (
                    <div className="dash-chart-loading"><Spinner color="primary" /></div>
                  ) : (
                    <div className="dash-entity-list-compact">
                      {(data?.topEntities || []).length === 0 && (
                        <p className="text-muted text-center my-3 fs-7">No data yet</p>
                      )}
                      {(data?.topEntities || []).slice(0, 4).map((e, i) => (
                        <div key={e.entity_id} className="dash-entity-item-compact">
                          <div className="dash-entity-rank">{i + 1}</div>
                          <div className="dash-entity-info">
                            <span className="dash-entity-name" title={e.entity_title}>{e.entity_title}</span>
                            <span className="dash-entity-type">
                              {e.entity_type === 1 ? "Broker" : e.entity_type === 2 ? "Agent" : "Company"}
                            </span>
                          </div>
                          <div className="dash-entity-bar-wrap">
                            <div
                              className="dash-entity-bar"
                              style={{
                                width: `${Math.round((e.deal_count / (data.topEntities[0]?.deal_count || 1)) * 100)}%`,
                                background: COLORS[i % COLORS.length],
                              }}
                            />
                          </div>
                          <strong className="dash-entity-count">{e.deal_count}</strong>
                        </div>
                      ))}
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* ── Row 3: Recently Joined Companies ──────────────────────── */}
          <Row className="g-3 mb-4">
            <Col lg={12}>
              <Card className="dash-chart-card">
                <CardBody className="p-3">
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <h6 className="dash-chart-title mb-0">Recently Joined Companies</h6>
                    <span className="dash-stat-sub mb-0">Latest onboarded entities</span>
                  </div>
                  {loading ? (
                    <div className="dash-chart-loading py-3"><Spinner color="primary" /></div>
                  ) : (
                    <div className="dash-recent-grid">
                      {(data?.recentEntities || []).length === 0 && (
                        <p className="text-muted text-center py-3 w-100 fs-7">No companies yet</p>
                      )}
                      {(data?.recentEntities || []).map((e) => (
                        <div key={e.entity_id} className="dash-recent-card-item">
                          <div className="dash-recent-avatar">
                            {(e.entity_title || "?")[0].toUpperCase()}
                          </div>
                          <div className="dash-recent-info">
                            <span className="dash-recent-name">{e.entity_title}</span>
                            <span className="dash-recent-email">{e.contact_email || "—"}</span>
                          </div>
                          <span className="dash-recent-date badge bg-light text-muted">
                            {e.created_at
                              ? new Date(e.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })
                              : "—"}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default Dashboard;
